<template>
  <div>
    <div v-if="deployable">
      <div class="card-body">
        <div class="row" v-if="hasPermission('claims.update') && !readonly">
          <div class="col-sm-4">
            <button
              class="btn btn-danger btn-block"
              v-on:click.prevent="reject"
              v-if="hasPermission('claims.delete')"
            >
              Reject Claim
            </button>
          </div>
          <div class="col-sm-4">
            <button
              class="btn btn-warning btn-block"
              v-on:click.prevent="toggleDiary"
            >
              Diary Claim
            </button>
          </div>
          <div class="col-sm-4">
            <button
              class="btn btn-primary btn-block"
              v-on:click.prevent="deploy"
            >
              Proceed to Deployment
            </button>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <fieldset v-if="hasClient" :disabled="readonly">
              <legend>Client - {{ client.full_name }}</legend>

              <questions
                :party="client"
                :questions="questions"
                type="client"
                :readonly="readonly"
                v-if="questions?.length"
              ></questions>
            </fieldset>
          </div>

          <div
            class="col-lg-6"
            v-bind:key="index"
            v-for="(thirdParty, index) in thirdParties"
          >
            <fieldset :disabled="readonly">
              <legend>Third Party - {{ thirdParty.full_name }}</legend>

              <questions
                :party="thirdParty"
                :questions="questions"
                type="third_party"
                :readonly="readonly"
                v-if="questions?.length"
              ></questions>
            </fieldset>
          </div>
        </div>
      </div>
      <div class="card-footer text-right" v-if="!readonly">
        <form-button :indicator="indicator" v-on:clicked="save">
          Save
        </form-button>
      </div>
    </div>

    <div class="text-center half-gutter-top half-gutter-bottom" v-else>
      <template v-if="claimType">
        <template v-if="claimType.name === 'windscreen'">
          <p>Windscreen claims are not handled within Axis</p>
          <p>
            Please contact Auto Windscreens on telephone number 0333 999 0100
          </p>
        </template>

        <template v-if="claimType.name === 'breakdown'">
          <p>Breakdown claims are not handled within Axis</p>
          <p>
            Please contact the relevant breakdown company to handle the
            breakdown
          </p>
        </template>

        <template v-if="claimType.name === 'policy_query'">
          <p>
            Thank you for collecting the policy query details, this case can now
            be closed
          </p>
        </template>

        <form-button
          :indicator="closingCase"
          v-on:clicked="closeCase"
          v-if="!readonly"
        >
          Close Case
        </form-button>
      </template>
    </div>
  </div>
</template>
<script>
import Questions from './Questions';

export default {
  props: {
    incident: {
      type: String,
      required: true
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  components: {
    questions: Questions
  },

  data() {
    return {
      diary: false,
      indicator: false,
      closingCase: false
    };
  },

  computed: {
    client() {
      return this.$store.getters['Incident/client'];
    },

    thirdParties() {
      return this.$store.getters['Incident/thirdParties'];
    },

    claimType() {
      return this.$store.getters['Incident/incidentClaimType'];
    },

    /**
     * Check that the assigned claim type has services that can be deployed.
     */
    deployable() {
      const name = this.claimType ? this.claimType.name : null;

      switch (name) {
        case 'windscreen':
        case 'breakdown':
        case 'policy_query':
          return false;

        default:
          return true;
      }
    },

    reasons() {
      return this.$store.getters['Incident/reasons'];
    },

    questions() {
      return this.$store.getters['QualificationQuestions/all'];
    },

    hasClient() {
      return Object.keys(this.client).length > 0;
    }
  },

  mounted() {
    this.mount();
  },

  methods: {
    mount() {
      this.$store.commit('QualificationQuestions/retrieve');

      if (!this.$store.getters['Incident/reasons'].length) {
        window.Ajax.get(`/incidents/reasons?filter[status]=rejected`)
          .then(response => {
            this.$store.commit('Incident/setReasons', response.data);
          })
          .catch(error => {});
      }
    },

    deploy() {
      if (this.readonly) return;
      if (!this.hasPermission('claims.update')) {
        return;
      }

      window
        .swal({
          title: 'Please Confirm',
          text: 'Are you sure you want to send this claim to deployment?',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No'
        })
        .then(confirm => {
          if (confirm) {
            window.Ajax.post(`/incidents/${this.incident}/validation`)
              .then(response => {
                window.toastify.success(
                  'Success',
                  'The claim was moved to deployment successfully!'
                );
                this.route(`/deployment/${this.incident}`);
              })
              .catch(error => {
                window.toastify.error(
                  'Error',
                  'Sorry, there was an issue rejecting this claim.'
                );
              });
          }
        })
        .catch(dismiss => {});
    },

    toggleDiary() {
      this.$emit('showDiary');
    },

    reject() {
      if (this.readonly) return;
      if (!this.hasPermission('claims.delete')) {
        return;
      }

      const options = {};

      this.reasons.forEach((reason, key) => {
        options[key] = reason.body;
      });

      window
        .swal({
          title: 'Please Confirm',
          text: 'Are you sure you want reject this claim?',
          type: 'warning',
          input: 'select',
          inputOptions: options,
          inputPlaceholder: 'Select a Reason',
          inputValidator: value => {
            return new Promise((resolve, reject) => {
              if (value) {
                resolve(value);
              } else {
                reject('You must select a reason.');
              }
            });
          },
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No'
        })
        .then(reason => {
          const reasonId = this.reasons[reason]?.id;

          if (reasonId) {
            window.Ajax.delete(`/incidents/${this.incident}?reason=${reasonId}`)
              .then(response => {
                window.toastify.success(
                  'Success',
                  'The claim was rejected successfully!'
                );
                this.route(`/qualification`);
              })
              .catch(error => {
                window.toastify.error(
                  'Error',
                  'Sorry, there was an issue rejecting this claim.'
                );
              });
          }
        })
        .catch(() => {});
    },

    closeCase() {
      if (this.readonly) return;
      this.closingCase = true;

      window.Ajax.post(`/incidents/${this.incident}/close`)
        .then(response => {
          window.toastify.success(
            'Success!',
            'The case was closed successfully!'
          );
          this.closingCase = false;
          this.$router.push('/dashboard');
        })
        .catch(error => {
          this.closingCase = false;
        });
    },

    save() {
      if (this.readonly) return;
      this.indicator = true;

      let client = this.saveClient();
      let thirdParties = this.saveThirdParties();

      Promise.all([client, thirdParties])
        .then(() => {
          this.indicator = false;
          window.toastify.success(
            'Saved',
            'The details were saved successfully!'
          );
        })
        .catch(() => {
          this.indicator = false;
          window.toastify.error(
            'Error',
            'There was an error saving the details'
          );
        });
    },

    saveClient() {
      return new Promise((resolve, reject) => {
        window.Ajax.post(
          `/incidents/${this.incident}/client/${this.client.identifier}/qualification`,
          this.client
        )
          .then(response => {
            resolve();
          })
          .catch(error => {
            reject();
          });
      });
    },

    saveThirdParties() {
      return new Promise((resolve, reject) => {
        let failure = false;
        for (let i = 0; i < this.thirdParties.length; i++) {
          if (!failure) {
            let thirdParty = this.thirdParties[i];

            window.Ajax.post(
              `/incidents/${this.incident}/third-parties/${thirdParty.identifier}/qualification`,
              thirdParty
            )
              .then(response => {})
              .catch(error => {
                failure = true;
              });
          } else {
            reject();
          }
        }

        if (!failure) {
          resolve();
        }
      });
    }
  }
};
</script>
